import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  Legend
} from 'recharts';
import './QubitChart.css';
import PlayerService from '../../services/PlayerService';

const QubitChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [chartType, setChartType] = useState('area'); // 'area', 'line', 'bar'
  const [viewMode, setViewMode] = useState('total'); // 'total' or 'volume'
  const [runningTotal, setRunningTotal] = useState(0);
  const [recalculating, setRecalculating] = useState(false);
  const [stats, setStats] = useState({
    currentTotal: 0,
    periodChange: 0,
    percentChange: 0,
    periodVolume: 0,
    dataPoints: 0,
    avgTransaction: 0,
    maxTransaction: 0,
    minTransaction: 0,
    highestTotal: 0,
    lowestTotal: 0
  });

  const apiUrl = process.env.REACT_APP_FIREBASE_API_URL;

  // Time range configurations
  const timeRanges = {
    '1h': { milliseconds: 60 * 60 * 1000, label: '1H', bucketSize: 60 * 1000 },
    '6h': { milliseconds: 6 * 60 * 60 * 1000, label: '6H', bucketSize: 5 * 60 * 1000 },
    '24h': { milliseconds: 24 * 60 * 60 * 1000, label: '24H', bucketSize: 30 * 60 * 1000 },
    '7d': { milliseconds: 7 * 24 * 60 * 60 * 1000, label: '7D', bucketSize: 6 * 60 * 60 * 1000 },
    '30d': { milliseconds: 30 * 24 * 60 * 60 * 1000, label: '30D', bucketSize: 24 * 60 * 60 * 1000 },
    'all': { milliseconds: Infinity, label: 'ALL', bucketSize: 7 * 24 * 60 * 60 * 1000 }
  };

  useEffect(() => {
    fetchChartData();
    fetchRunningTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const fetchRunningTotal = async () => {
    try {
      const response = await fetch(`${apiUrl}/qubits/runningTotal.json`);
      const total = await response.json();
      setRunningTotal(Number(total) || 0);
    } catch (err) {
      console.error('Error fetching running total:', err);
    }
  };

  const recalculateRunningTotal = async () => {
    setRecalculating(true);
    try {
      const allPlayers = await PlayerService.getAllPlayers();
      const totalQubits = allPlayers.reduce((sum, player) => sum + (player.qubits || 0), 0);

      await fetch(`${apiUrl}/qubits/runningTotal.json`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(totalQubits)
      });

      setRunningTotal(totalQubits);
    } catch (err) {
      console.error('Error recalculating running total:', err);
    } finally {
      setRecalculating(false);
    }
  };

  const fetchChartData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiUrl}/qubits/transactions.json`);
      if (!response.ok) throw new Error('Failed to fetch transaction data');

      const transactions = await response.json();

      if (!transactions) {
        setChartData([]);
        setStats({
          currentTotal: 0,
          periodChange: 0,
          percentChange: 0,
          periodVolume: 0,
          dataPoints: 0,
          avgTransaction: 0,
          maxTransaction: 0,
          minTransaction: 0,
          highestTotal: 0,
          lowestTotal: 0
        });
        setLoading(false);
        return;
      }

      // Convert to array and sort by timestamp
      const transactionArray = Object.entries(transactions).map(([id, data]) => ({
        id,
        ...data
      })).sort((a, b) => a.timestamp - b.timestamp);

      // Build running totals
      const firstWithTotal = transactionArray.find(t => t.runningTotal !== undefined && t.runningTotal !== null);

      let allDataPoints;
      if (firstWithTotal) {
        const firstIndex = transactionArray.indexOf(firstWithTotal);
        const sumBeforeFirst = transactionArray
          .slice(0, firstIndex)
          .reduce((sum, t) => sum + t.amount, 0);
        const baseTotal = firstWithTotal.runningTotal - firstWithTotal.amount - sumBeforeFirst;

        let runningTotal = baseTotal;
        allDataPoints = transactionArray.map((transaction) => {
          runningTotal += transaction.amount;
          if (transaction.runningTotal !== undefined && transaction.runningTotal !== null) {
            runningTotal = transaction.runningTotal;
          }
          return {
            timestamp: transaction.timestamp,
            total: runningTotal,
            amount: transaction.amount,
            userId: transaction.userId
          };
        });
      } else {
        let runningTotal = 0;
        allDataPoints = transactionArray.map(transaction => {
          runningTotal += transaction.amount;
          return {
            timestamp: transaction.timestamp,
            total: runningTotal,
            amount: transaction.amount,
            userId: transaction.userId
          };
        });
      }

      // Filter to time range
      const now = Date.now();
      const cutoffTime = timeRange === 'all' ? 0 : now - timeRanges[timeRange].milliseconds;
      const filteredData = allDataPoints.filter(point => point.timestamp >= cutoffTime);

      if (filteredData.length === 0) {
        setChartData([]);
        setStats({
          currentTotal: 0,
          periodChange: 0,
          percentChange: 0,
          periodVolume: 0,
          dataPoints: 0,
          avgTransaction: 0,
          maxTransaction: 0,
          minTransaction: 0,
          highestTotal: 0,
          lowestTotal: 0
        });
        setLoading(false);
        return;
      }

      // Bucket the data for better visualization
      let processedData;
      const bucketSize = timeRanges[timeRange].bucketSize;

      if (filteredData.length < 50) {
        // Use individual data points if we don't have many
        processedData = filteredData.map(point => ({
          time: point.timestamp,
          total: point.total,
          amount: point.amount,
          count: 1
        }));
      } else {
        // Bucket the data
        const buckets = new Map();

        filteredData.forEach(point => {
          const bucketKey = Math.floor(point.timestamp / bucketSize) * bucketSize;
          if (!buckets.has(bucketKey)) {
            buckets.set(bucketKey, {
              transactions: [],
              totalAmount: 0,
              count: 0
            });
          }
          const bucket = buckets.get(bucketKey);
          bucket.transactions.push(point);
          bucket.totalAmount += point.amount;
          bucket.count += 1;
        });

        processedData = Array.from(buckets.entries())
          .map(([bucketTime, bucket]) => {
            const lastPoint = bucket.transactions[bucket.transactions.length - 1];
            return {
              time: bucketTime,
              total: lastPoint.total,
              amount: bucket.totalAmount,
              count: bucket.count
            };
          })
          .sort((a, b) => a.time - b.time);
      }

      // Calculate statistics
      const currentTotal = filteredData[filteredData.length - 1].total;
      const startTotal = filteredData[0].total;
      const periodChange = currentTotal - startTotal;
      const percentChange = startTotal > 0 ? ((periodChange / startTotal) * 100) : 0;
      const periodVolume = filteredData.reduce((sum, d) => sum + (d.amount || 0), 0);
      const amounts = filteredData.map(d => d.amount);
      const totals = filteredData.map(d => d.total);

      setStats({
        currentTotal,
        periodChange,
        percentChange,
        periodVolume,
        dataPoints: filteredData.length,
        avgTransaction: amounts.length > 0 ? periodVolume / amounts.length : 0,
        maxTransaction: amounts.length > 0 ? Math.max(...amounts) : 0,
        minTransaction: amounts.length > 0 ? Math.min(...amounts) : 0,
        highestTotal: totals.length > 0 ? Math.max(...totals) : 0,
        lowestTotal: totals.length > 0 ? Math.min(...totals) : 0
      });

      setChartData(processedData);
      setLoading(false);

    } catch (err) {
      console.error('Chart data fetch error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toLocaleString();
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    if (timeRange === '1h' || timeRange === '6h') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (timeRange === '24h') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (timeRange === '7d') {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip-new">
          <div className="tooltip-header">
            {new Date(data.time).toLocaleString()}
          </div>
          <div className="tooltip-body">
            <div className="tooltip-row">
              <span className="tooltip-label">Total:</span>
              <span className="tooltip-value total">{formatNumber(data.total)}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label">Volume:</span>
              <span className="tooltip-value volume">+{formatNumber(data.amount)}</span>
            </div>
            {data.count > 1 && (
              <div className="tooltip-row">
                <span className="tooltip-label">Transactions:</span>
                <span className="tooltip-value">{data.count}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="qubit-chart-container">
        <div className="chart-loading-state">
          <div className="loading-spinner-new">⟳</div>
          <div className="loading-text">LOADING QUBIT DATA...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="qubit-chart-container">
        <div className="chart-error-state">
          <div className="error-icon-new">⚠</div>
          <div className="error-text">ERROR LOADING DATA</div>
          <div className="error-details">{error}</div>
        </div>
      </div>
    );
  }

  const isPositiveChange = stats.percentChange >= 0;

  return (
    <div className="qubit-chart-container">
      {/* Main Stats Header */}
      <div className="chart-main-header">
        <div className="chart-title-block">
          <h2>NETWORK QUBIT VOLUME</h2>
          <p className="chart-description">Real-time qubit accumulation across all network nodes</p>
        </div>

        <div className="chart-primary-stat">
          <div className="primary-stat-label">CURRENT TOTAL</div>
          <div className="primary-stat-value">{formatNumber(stats.currentTotal)}</div>
          <div className={`primary-stat-change ${isPositiveChange ? 'positive' : 'negative'}`}>
            {isPositiveChange ? '▲' : '▼'} {formatNumber(Math.abs(stats.periodChange))} ({isPositiveChange ? '+' : ''}{stats.percentChange.toFixed(2)}%)
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="chart-controls-bar">
        <div className="time-range-buttons">
          {Object.entries(timeRanges).map(([key, config]) => (
            <button
              key={key}
              className={`time-btn ${timeRange === key ? 'active' : ''}`}
              onClick={() => setTimeRange(key)}
            >
              {config.label}
            </button>
          ))}
        </div>

        <div className="chart-controls-right">
          <div className="view-mode-buttons">
            <button
              className={`view-mode-btn ${viewMode === 'total' ? 'active' : ''}`}
              onClick={() => setViewMode('total')}
            >
              TOTAL
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'volume' ? 'active' : ''}`}
              onClick={() => setViewMode('volume')}
            >
              VOLUME
            </button>
          </div>

          <div className="chart-type-buttons">
            <button
              className={`chart-type-btn ${chartType === 'area' ? 'active' : ''}`}
              onClick={() => setChartType('area')}
              title="Area Chart"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 20l4-8 4 4 6-12 4 8"/>
                <path d="M3 20h18" strokeWidth="1"/>
              </svg>
            </button>
            <button
              className={`chart-type-btn ${chartType === 'line' ? 'active' : ''}`}
              onClick={() => setChartType('line')}
              title="Line Chart"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 20 7 12 11 16 17 4 21 12"></polyline>
              </svg>
            </button>
            <button
              className={`chart-type-btn ${chartType === 'bar' ? 'active' : ''}`}
              onClick={() => setChartType('bar')}
              title="Volume Bars"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="12" width="3" height="8"></rect>
                <rect x="7" y="8" width="3" height="12"></rect>
                <rect x="11" y="14" width="3" height="6"></rect>
                <rect x="15" y="6" width="3" height="14"></rect>
                <rect x="19" y="10" width="3" height="10"></rect>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Chart Display */}
      <div className="chart-display-area">
        {chartData.length === 0 ? (
          <div className="chart-empty-state">
            <div className="empty-icon-new">📊</div>
            <div className="empty-title">NO DATA AVAILABLE</div>
            <div className="empty-subtitle">Qubit transactions will appear here once users start harvesting</div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'area' ? (
              <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#33ff33" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#33ff33" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(51, 255, 51, 0.15)" />
                <XAxis
                  dataKey="time"
                  tickFormatter={formatTimestamp}
                  stroke="rgba(51, 255, 51, 0.6)"
                  style={{ fontSize: '12px', fontFamily: 'IBM BIOS, monospace' }}
                />
                <YAxis
                  tickFormatter={formatNumber}
                  stroke="rgba(51, 255, 51, 0.6)"
                  style={{ fontSize: '12px', fontFamily: 'IBM BIOS, monospace' }}
                  domain={viewMode === 'total'
                    ? ['dataMin - (dataMax - dataMin) * 0.05', 'dataMax + (dataMax - dataMin) * 0.05']
                    : [0, 'dataMax + dataMax * 0.1']}
                  scale="linear"
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey={viewMode === 'total' ? 'total' : 'amount'}
                  stroke="#33ff33"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorTotal)"
                />
              </AreaChart>
            ) : chartType === 'line' ? (
              <LineChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(51, 255, 51, 0.15)" />
                <XAxis
                  dataKey="time"
                  tickFormatter={formatTimestamp}
                  stroke="rgba(51, 255, 51, 0.6)"
                  style={{ fontSize: '12px', fontFamily: 'IBM BIOS, monospace' }}
                />
                <YAxis
                  tickFormatter={formatNumber}
                  stroke="rgba(51, 255, 51, 0.6)"
                  style={{ fontSize: '12px', fontFamily: 'IBM BIOS, monospace' }}
                  domain={viewMode === 'total'
                    ? ['dataMin - (dataMax - dataMin) * 0.05', 'dataMax + (dataMax - dataMin) * 0.05']
                    : [0, 'dataMax + dataMax * 0.1']}
                  scale="linear"
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey={viewMode === 'total' ? 'total' : 'amount'}
                  stroke="#33ff33"
                  strokeWidth={2}
                  dot={chartData.length < 50 ? { fill: '#33ff33', strokeWidth: 2, r: 4 } : false}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            ) : (
              <BarChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(51, 255, 51, 0.15)" />
                <XAxis
                  dataKey="time"
                  tickFormatter={formatTimestamp}
                  stroke="rgba(51, 255, 51, 0.6)"
                  style={{ fontSize: '12px', fontFamily: 'IBM BIOS, monospace' }}
                />
                <YAxis
                  tickFormatter={formatNumber}
                  stroke="rgba(51, 255, 51, 0.6)"
                  style={{ fontSize: '12px', fontFamily: 'IBM BIOS, monospace' }}
                  domain={[0, 'dataMax + dataMax * 0.1']}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="amount" fill="rgba(51, 255, 51, 0.7)" />
              </BarChart>
            )}
          </ResponsiveContainer>
        )}
      </div>

      {/* Detailed Statistics Grid */}
      <div className="chart-stats-grid">
        <div className="stat-card">
          <div className="stat-card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          </div>
          <div className="stat-card-content">
            <div className="stat-card-label">PERIOD VOLUME</div>
            <div className="stat-card-value">{formatNumber(stats.periodVolume)}</div>
            <div className="stat-card-sublabel">Total qubits generated</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <div className="stat-card-content">
            <div className="stat-card-label">GLOBAL TOTAL</div>
            <div className="stat-card-value">{formatNumber(runningTotal)}</div>
            <button
              className="sync-btn"
              onClick={recalculateRunningTotal}
              disabled={recalculating}
            >
              {recalculating ? '⟳ SYNCING...' : '↻ SYNC'}
            </button>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
          </div>
          <div className="stat-card-content">
            <div className="stat-card-label">DATA POINTS</div>
            <div className="stat-card-value">{stats.dataPoints.toLocaleString()}</div>
            <div className="stat-card-sublabel">Transactions recorded</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <div className="stat-card-content">
            <div className="stat-card-label">AVG TRANSACTION</div>
            <div className="stat-card-value">{formatNumber(stats.avgTransaction)}</div>
            <div className="stat-card-sublabel">Average qubits per tx</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00ff88" strokeWidth="2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
              <polyline points="17 6 23 6 23 12"></polyline>
            </svg>
          </div>
          <div className="stat-card-content">
            <div className="stat-card-label">HIGHEST TX</div>
            <div className="stat-card-value highlight-green">{formatNumber(stats.maxTransaction)}</div>
            <div className="stat-card-sublabel">Peak transaction</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ff8800" strokeWidth="2">
              <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
              <polyline points="17 18 23 18 23 12"></polyline>
            </svg>
          </div>
          <div className="stat-card-content">
            <div className="stat-card-label">LOWEST TX</div>
            <div className="stat-card-value highlight-orange">{formatNumber(stats.minTransaction)}</div>
            <div className="stat-card-sublabel">Minimum transaction</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QubitChart;
